import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useUpdateProductStatus } from "./useActionButton";
import { PRODUCT_STATUS } from "../../../../models/Product";
import { rootPaths } from "../../../../routes/routePaths";
import Text from "../../../atoms/Text";

type RejectForm = {
  reason: string;
};

const defaultReasonValue = {
  reason: "",
};

type Props = {
  productId: string;
  customStyle?: SxProps<Theme>;
};

const ButtonActionReject = ({ productId, customStyle }: Props) => {
  const navigate = useNavigate();
  const formMethods = useForm<RejectForm>({
    defaultValues: defaultReasonValue,
  });

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { updateProductStatus, isLoading } = useUpdateProductStatus();

  //initiating the form.
  useEffect(() => {
    formMethods.reset(defaultReasonValue);
  }, [isModalOpen]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleReject = async () => {
    const reason = formMethods.getValues("reason");
    await updateProductStatus(productId, PRODUCT_STATUS.REJECTED, reason);
    handleCloseModal();
    navigate(rootPaths.rewardInventoryPortal);
  };

  return (
    <Box data-testid="action-reject-component">
      <Button
        variant="secondary"
        size="small"
        onClick={handleOpenModal}
        sx={customStyle}
        data-testid="reject-button"
      >
        Reject
      </Button>

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        data-testid="modal-component"
      >
        <Box
          width={532}
          bgcolor="mono.white"
          borderRadius={2.5}
          pb={3}
          overflow="hidden"
        >
          <Box px={2.5} py={2} bgcolor="mono.light.grey">
            <Text variant={"title_medium"}>Reject Item</Text>
          </Box>
          <form onSubmit={formMethods.handleSubmit(handleReject)}>
            <Stack p={3}>
              <Text variant={"body_large"}>
                Please enter your reject reason
              </Text>
              <TextField
                {...formMethods.register("reason", {
                  required: true,
                })}
                multiline
                name={"reason"}
                label={"Reason"}
                rows={4}
              />
            </Stack>
            <Stack p={3} alignItems="center">
              <Stack direction="row" spacing={1} width={256}>
                <Button
                  variant="secondary"
                  size="small"
                  fullWidth={true}
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  fullWidth={true}
                  disabled={isLoading}
                  startIcon={isLoading && <CircularProgress size={20} />}
                  type="submit"
                >
                  Submit
                </Button>
              </Stack>
            </Stack>
          </form>
        </Box>
      </Modal>
    </Box>
  );
};

export default ButtonActionReject;
